import { useState } from "react";
import { Mail, Phone, MapPin, Clock, Send, Building2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { COMPANY } from "@/lib/company";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim())
      return toast.error("Please fill in your name, email and message");
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! We've received your message and will get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const details = [
    { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { icon: Phone, label: "Phone", value: COMPANY.phone, href: `tel:${COMPANY.phone.replace(/\s/g, "")}` },
    { icon: MapPin, label: "Address", value: COMPANY.address },
    { icon: Clock, label: "Hours", value: COMPANY.hours },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="contact-page">
      <Navbar />
      <div className="bg-primary">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
          <p className="overline text-accent mb-3">We're here to help</p>
          <h1 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">Contact Us</h1>
          <p className="text-white/70 mt-3 max-w-xl">Questions about a booking, refund or a route? Send us a message and our team will respond during business hours.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12 grid gap-8 lg:grid-cols-[1fr_360px]">
        {/* Form */}
        <form onSubmit={submit} className="bg-white border border-border rounded-xl p-6 sm:p-8 space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" data-testid="contact-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" data-testid="contact-email" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="How can we help?" data-testid="contact-subject" />
          </div>
          <div className="space-y-1.5">
            <Label>Message</Label>
            <Textarea rows={6} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Write your message, including your booking reference if any…" data-testid="contact-message" />
          </div>
          <Button type="submit" className="bg-accent hover:bg-accent/90 text-white font-semibold" disabled={loading} data-testid="contact-submit">
            {loading ? "Sending…" : <><Send className="h-4 w-4 mr-2" /> Send message</>}
          </Button>
        </form>

        {/* Details */}
        <div className="space-y-4">
          <div className="bg-white border border-border rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="h-5 w-5 text-accent" />
              <span className="font-display font-semibold text-primary">{COMPANY.legal}</span>
            </div>
            <ul className="space-y-4">
              {details.map((d) => (
                <li key={d.label} className="flex gap-3" data-testid={`contact-detail-${d.label}`}>
                  <d.icon className="h-5 w-5 text-accent shrink-0 mt-0.5" strokeWidth={1.75} />
                  <div>
                    <div className="overline text-muted-foreground">{d.label}</div>
                    {d.href ? (
                      <a href={d.href} className="text-primary font-medium hover:text-accent transition-colors">{d.value}</a>
                    ) : (
                      <div className="text-primary font-medium">{d.value}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-primary text-white/80 rounded-xl p-6 text-sm">
            <p className="font-display font-semibold text-white mb-1">Grievance Officer</p>
            <p>For escalations, email <a href={`mailto:${COMPANY.grievanceEmail}`} className="text-accent font-medium">{COMPANY.grievanceEmail}</a>.</p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
